import { useLayoutEffect, useRef, useState } from "react";
import { ChevronDown, Sparkles } from "lucide-react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface FeatureRequestAIAnalysisCardProps {
  analysis: string;
  analysisBy: string;
  analysisAt: string | null;
  formatDateTime: (value: string) => string;
}

// Admin-only card shown under the request description with the triage agent's
// research. The backend only includes the analysis for managers.
export default function FeatureRequestAIAnalysisCard({
  analysis,
  analysisBy,
  analysisAt,
  formatDateTime,
}: FeatureRequestAIAnalysisCardProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (el) setOverflows(el.scrollHeight > el.clientHeight);
  }, [analysis, collapsed]);

  return (
    <div className="rounded-md border border-violet-200 dark:border-violet-700 bg-violet-50 dark:bg-violet-900/20">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={!collapsed}
      >
        <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-300 flex-shrink-0" />
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          AI analysis
        </span>
        <span className="inline-flex items-center rounded-full bg-violet-100 dark:bg-violet-900/40 px-2 py-0.5 text-xs font-medium text-violet-800 dark:text-violet-300">
          Admins only
        </span>
        <ChevronDown
          className={`ml-auto h-4 w-4 text-gray-400 transition-transform ${collapsed ? "" : "rotate-180"}`}
        />
      </button>

      {!collapsed && (
        <div className="px-3 pb-3">
          <div className="relative">
            <div
              ref={bodyRef}
              className={`prose prose-sm dark:prose-invert max-w-none prose-h1:text-base prose-h2:text-sm prose-h3:text-sm prose-a:text-violet-700 dark:prose-a:text-violet-300 ${showFull ? "" : "max-h-64 overflow-hidden"}`}
            >
              <Markdown remarkPlugins={[remarkGfm]}>{analysis}</Markdown>
            </div>
            {!showFull && overflows && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-violet-50 dark:from-violet-950 to-transparent" />
            )}
          </div>
          {(overflows || showFull) && (
            <button
              type="button"
              className="mt-1 text-xs font-medium text-violet-700 dark:text-violet-300 underline"
              onClick={() => setShowFull((v) => !v)}
            >
              {showFull ? "Collapse analysis" : "Show full analysis"}
            </button>
          )}
          <p className="mt-2 text-xs text-violet-700 dark:text-violet-300">
            {analysisBy}
            {analysisAt ? ` · ${formatDateTime(analysisAt)}` : ""}
          </p>
        </div>
      )}
    </div>
  );
}
